import React, { useState, useCallback } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Upload, FileText, CheckCircle, AlertCircle, Loader2, 
  TrendingUp, TrendingDown, Building2, Eye, Trash2, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import axios from "axios";

export const CASImportPage = () => {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [loadingInvestors, setLoadingInvestors] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [loadingHoldings, setLoadingHoldings] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fetchInvestors = useCallback(async () => {
    setLoadingInvestors(true);
    try {
      const res = await axios.get(`${API}/portfolio/investors`);
      setInvestors(res.data.investors || []);
    } catch (error) {
      console.error("Error fetching investors:", error);
    } finally {
      setLoadingInvestors(false);
    }
  }, []);

  React.useEffect(() => {
    fetchInvestors();
  }, [fetchInvestors]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === "application/pdf") {
        setFile(droppedFile);
      } else {
        toast.error("Please upload a PDF file");
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a CAS PDF file");
      return;
    }
    if (!password) {
      toast.error("Please enter the PDF password (usually your PAN)");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("password", password);

      const res = await axios.post(`${API}/portfolio/import-cas`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setResult(res.data);
      toast.success(`Imported ${res.data.holdings_count} holdings successfully!`);
      fetchInvestors();
      setFile(null);
      setPassword("");
    } catch (error) {
      console.error("Error importing CAS:", error);
      const message = error.response?.data?.detail || "Failed to import CAS";
      toast.error(message);
      setResult({ success: false, error: message });
    } finally {
      setLoading(false);
    }
  };

  const viewHoldings = async (pan) => {
    setSelectedInvestor(pan);
    setLoadingHoldings(true);
    try {
      const res = await axios.get(`${API}/portfolio/holdings/${pan}`);
      setHoldings(res.data.holdings || []);
    } catch (error) {
      console.error("Error fetching holdings:", error);
      toast.error("Failed to fetch holdings");
    } finally {
      setLoadingHoldings(false);
    }
  };

  const deleteInvestor = async (pan) => {
    if (!confirm(`Delete all holdings for PAN ${pan.slice(0, 4)}****?`)) return;
    
    try {
      await axios.delete(`${API}/portfolio/holdings/${pan}`);
      toast.success("Holdings deleted");
      fetchInvestors();
      if (selectedInvestor === pan) {
        setSelectedInvestor(null);
        setHoldings([]);
      }
    } catch (error) {
      toast.error("Failed to delete holdings");
    }
  };

  const formatCurrency = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">
            CAS <span className="gold-text">Import</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Import customer portfolios from CAMS/KFintech Consolidated Account Statements
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Upload Section */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-primary" />
                Upload CAS PDF
              </CardTitle>
              <CardDescription>
                Upload a CAMS or KFintech CAS PDF to import holdings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Drag & Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                  dragActive 
                    ? "border-primary bg-primary/5" 
                    : file 
                      ? "border-green-500 bg-green-500/5" 
                      : "border-border hover:border-primary/50"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {file ? (
                  <div className="space-y-2">
                    <FileText className="w-12 h-12 mx-auto text-green-500" />
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setFile(null)}
                      className="text-red-500 hover:text-red-600"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="w-12 h-12 mx-auto text-muted-foreground" />
                    <div>
                      <p className="font-medium">Drop your CAS PDF here</p>
                      <p className="text-sm text-muted-foreground">or click to browse</p>
                    </div>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      id="cas-file-input"
                    />
                    <Button asChild variant="outline">
                      <label htmlFor="cas-file-input" className="cursor-pointer">
                        Browse Files
                      </label>
                    </Button>
                  </div>
                )}
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <Label htmlFor="password">PDF Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password (usually PAN number)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="cas-password-input"
                />
                <p className="text-xs text-muted-foreground">
                  CAS PDFs are password protected with the investor's PAN number
                </p>
              </div>

              {/* Upload Button */}
              <Button 
                onClick={handleUpload} 
                disabled={!file || !password || loading}
                className="w-full"
                data-testid="import-cas-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Holdings
                  </>
                )}
              </Button>

              {/* Result Display */}
              {result && (
                <div className={`p-4 rounded-lg ${
                  result.success 
                    ? "bg-green-500/10 border border-green-500/20" 
                    : "bg-red-500/10 border border-red-500/20"
                }`}>
                  {result.success ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-green-500">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Import Successful!</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Investor</p>
                          <p className="font-medium">{result.investor?.name || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Schemes</p>
                          <p className="font-medium">{result.holdings_count}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Total Value</p>
                          <p className="font-medium text-green-500">
                            {formatCurrency(result.summary?.total_value || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Total Gain</p>
                          <p className={`font-medium ${
                            (result.summary?.total_gain || 0) >= 0 ? "text-green-500" : "text-red-500"
                          }`}>
                            {formatCurrency(result.summary?.total_gain || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-500">
                      <AlertCircle className="w-5 h-5" />
                      <span>{result.error}</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Investors List */}
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Imported Portfolios
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={fetchInvestors}
                  disabled={loadingInvestors}
                >
                  <RefreshCw className={`w-4 h-4 ${loadingInvestors ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingInvestors ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : investors.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No portfolios imported yet</p>
                  <p className="text-sm">Upload a CAS PDF to get started</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {investors.map((inv) => (
                    <div 
                      key={inv.pan}
                      className={`p-4 rounded-lg border transition-all cursor-pointer ${
                        selectedInvestor === inv.pan 
                          ? "border-primary bg-primary/5" 
                          : "border-border hover:border-primary/50"
                      }`}
                      onClick={() => viewHoldings(inv.pan)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{inv.name || "Unknown"}</p>
                          <p className="text-sm text-muted-foreground">
                            PAN: {inv.pan?.slice(0, 4)}****
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-green-500">
                            {formatCurrency(inv.total_value || 0)}
                          </p>
                          <p className={`text-xs ${
                            (inv.gain_percentage || 0) >= 0 ? "text-green-500" : "text-red-500"
                          }`}>
                            {(inv.gain_percentage || 0) >= 0 ? "+" : ""}
                            {(inv.gain_percentage || 0).toFixed(1)}%
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                        <span>{inv.total_schemes} schemes</span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              viewHoldings(inv.pan);
                            }}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-red-500 hover:text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteInvestor(inv.pan);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Holdings Table */}
        {selectedInvestor && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>
                Holdings for PAN: {selectedInvestor.slice(0, 4)}****
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingHoldings ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : holdings.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No holdings found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Scheme</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">AMC</th>
                        <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Units</th>
                        <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">NAV</th>
                        <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Value</th>
                        <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Gain</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holdings.map((h, idx) => (
                        <tr key={idx} className="border-b border-border/50 hover:bg-secondary/30">
                          <td className="py-3 px-2">
                            <p className="font-medium text-sm line-clamp-1">{h.scheme_name}</p>
                            <p className="text-xs text-muted-foreground">Folio: {h.folio}</p>
                          </td>
                          <td className="py-3 px-2 text-sm text-muted-foreground">{h.amc}</td>
                          <td className="py-3 px-2 text-right font-mono text-sm">{h.units?.toFixed(3)}</td>
                          <td className="py-3 px-2 text-right font-mono text-sm">₹{h.nav?.toFixed(2)}</td>
                          <td className="py-3 px-2 text-right font-mono text-sm font-medium">
                            {formatCurrency(h.current_value || 0)}
                          </td>
                          <td className="py-3 px-2 text-right">
                            <div className={`flex items-center justify-end gap-1 ${
                              (h.gain || 0) >= 0 ? "text-green-500" : "text-red-500"
                            }`}>
                              {(h.gain || 0) >= 0 ? (
                                <TrendingUp className="w-3 h-3" />
                              ) : (
                                <TrendingDown className="w-3 h-3" />
                              )}
                              <span className="font-mono text-sm">
                                {(h.gain_percentage || 0).toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-3">How to get CAS PDF</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Visit <a href="https://www.camsonline.com/Investors/Statements/Consolidated-Account-Statement" target="_blank" rel="noopener" className="text-primary hover:underline">CAMS Online</a> or <a href="https://mfs.kfintech.com/investor/General/ConsolidatedAccountStatement" target="_blank" rel="noopener" className="text-primary hover:underline">KFintech</a></li>
              <li>Enter investor's email and PAN number</li>
              <li>Select "Detailed" statement type for complete transaction history</li>
              <li>The PDF will be sent to the registered email</li>
              <li>Upload the PDF here with PAN as password</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
